import React, { useState } from "react";
import { contactInfo } from "../data/mock";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    program: "",
    message: "",
  });

  const whatsappNumber = "919303998387";

  const whatsappMessage = `Hi! I'm interested in your programs.
Name: ${formData.name || "___"}
Email: ${formData.email || "___"}
Program: ${formData.program || "___"}`;

  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
    whatsappMessage
  )}`;

  const encode = (data) =>
    Object.keys(data)
      .map(
        (key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key])
      )
      .join("&");

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "contact",
          ...formData,
        }),
      });

      toast.success("Thank you! We will contact you soon.");

      setFormData({
        name: "",
        email: "",
        phone: "",
        program: "",
        message: "",
      });
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section
      id="contact"
      style={{
        background: "var(--bg-primary)",
        padding: "100px 7.6923%",
        borderTop: "1px solid var(--border-subtle)",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        
        {/* SEO Optimized Context Header */}
        <div style={{ textAlign: "center", marginBottom: "60px" }}>
          <h2 className="display-large" style={{ marginBottom: "20px" }}>
            Contact Ciqura Labs Indore Branch
          </h2>
          <p className="body-large" style={{ color: "var(--text-secondary)", maxWidth: "800px", margin: "0 auto" }}>
            Get in touch with our career counseling desk in Vijay Nagar to explore professional cybersecurity tracks, production-grade Python training, or corporate placement support.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "60px",
          }}
        >
          {/* CONTACT INFO CONTAINER */}
          <div>
            <h3 className="heading-1" style={{ marginBottom: "30px" }}>
              Admissions Office & Lab Info
            </h3>

            <div style={{ display: "flex", flexDirection: "column", gap: "30px" }}>
              <div style={{ display: "flex", gap: "16px" }}>
                <Phone size={24} style={{ color: "var(--brand-primary)" }} />
                <div>
                  <h4 className="heading-3">Admissions Hotline</h4>
                  <a href={`tel:${contactInfo.phone}`} className="body-medium" style={{ color: "var(--text-secondary)", textDecoration: "none", fontWeight: "600" }}>
                    {contactInfo.phone}
                  </a>
                </div>
              </div>

              <div style={{ display: "flex", gap: "16px" }}>
                <Mail size={24} style={{ color: "var(--brand-primary)" }} />
                <div>
                  <h4 className="heading-3">Corporate Email Channel</h4>
                  <a href={`mailto:${contactInfo.email}`} className="body-medium" style={{ color: "var(--text-secondary)", textDecoration: "none" }}>
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              <div style={{ display: "flex", gap: "16px" }}>
                <MapPin size={24} style={{ color: "var(--brand-primary)" }} />
                <div>
                  <h4 className="heading-3">Vijay Nagar Lab Center</h4>
                  <p className="body-medium" style={{ color: "var(--text-secondary)", lineHeight: "1.6" }}>{contactInfo.address}</p>
                </div>
              </div>

              {/* WHATSAPP BUTTON */}
              <Button
                type="button"
                className="btn-primary"
                onClick={() => window.open(whatsappLink, "_blank")}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "14px 24px",
                  width: "fit-content",
                  alignSelf: "flex-start",
                }}
              >
                <FaWhatsapp size={22} />
                <span>WhatsApp Live Chat</span>
              </Button>
            </div>
          </div>

          {/* CONTACT FORM CONTAINER */}
          <div
            style={{
              background: "var(--bg-secondary)",
              padding: "40px",
              border: "1px solid var(--border-subtle)",
            }}
          >
            <h3 className="heading-1" style={{ marginBottom: "30px" }}>
              Send an Enrollment Message
            </h3>

            <form
              name="contact"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <input type="hidden" name="form-name" value="contact" />

              <div style={{ display: "none" }}>
                <label>
                  Don’t fill this out: <input name="bot-field" />
                </label>
              </div>

              <Input
                name="name"
                placeholder="Your Full Name"
                value={formData.name}
                onChange={handleChange}
                required
              />

              <Input
                type="email"
                name="email"
                placeholder="Your Enterprise Email Address"
                value={formData.email}
                onChange={handleChange}
                required
              />

              <Input
                type="tel"
                name="phone"
                placeholder="Active WhatsApp Phone Number"
                value={formData.phone}
                onChange={handleChange}
                required
              />

              <select
                name="program"
                value={formData.program}
                onChange={handleChange}
                required
                style={{
                  background: "var(--bg-primary)",
                  border: "1px solid var(--border-medium)",
                  color: "var(--text-primary)",
                  padding: "14px",
                  borderRadius: "4px"
                }}
              >
                <option value="">Select an Educational Track</option>
                <option value="cybersecurity">Cybersecurity & Ethical Hacking Track</option>
                <option value="networking">IT Support & Cisco Networking Track</option>
                <option value="azure">Microsoft Azure & Cloud Security Track</option>
                <option value="aiml">AI & Machine Learning (AIML) with Python</option>
                <option value="grc">Governance, Risk & Compliance (GRC) Track</option>
                <option value="Software & Web Development">Software & MERN Full Stack Development</option>
              </select>

              <Textarea
                name="message"
                placeholder="Outline your background, learning objectives, or placement goals..."
                value={formData.message}
                onChange={handleChange}
                rows={4}
              />

              <Button type="submit" className="btn-primary">
                Send Message <Send size={18} />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;